import * as moment from "moment-timezone";
import {
  findIncidentsBySchedules,
  findPartnerNoProgramadosPayroll,
} from "../services/incident.service.ts";
import type { Unschedule } from "../models/Incidents.ts";

const incidentRowKey = (incident: {
  schedule?: { id?: number };
  status?: string;
}) => `${incident.schedule?.id}-${incident.status}`;

function dedupeUnschedule(items: Unschedule[]): Unschedule[] {
  const seen = new Set<string>();
  const out: Unschedule[] = [];
  for (const item of items || []) {
    if (!item?.partner?.id || !item?.checkIn?.id) continue;
    const key = `${item.partner.id}-${item.checkIn.id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(item);
  }
  return out;
}

/** Fila de no programado pendiente (sin guardar en BD) para la tabla de nómina. */
export function mapNoProgramadoToPayrollIncident(np: Unschedule): any {
  const checkDate = np.checkIn?.checkDate;
  return {
    id: `no-programado-${np.partner?.id}-${np.checkIn?.id}`,
    status: "No programado",
    type: "Aceptar",
    reason: "",
    finalStatus: null,
    authorizeBy: null,
    isUnscheduledPending: true,
    checkIn: np.checkIn,
    checkOut: np.checkOut,
    schedule: {
      id: 0,
      exactDate: checkDate,
      startTime: "—",
      endTime: "—",
      partner: np.partner,
      branch: (np as any).branch ?? null,
      branchId: (np as any).branchId ?? (np as any).branch?.id,
      checkIn: np.checkIn,
      checkOut: np.checkOut,
      assistanceCheck: np.checkIn,
      assistanceCheckout: np.checkOut,
    },
  };
}

function sortIncidentsByDate(rows: any[]): any[] {
  return [...rows].sort((a, b) => {
    const dateAStr =
      a.schedule?.assistanceCheck?.checkDate ||
      a.schedule?.exactDate ||
      a.checkIn?.checkDate;
    const dateBStr =
      b.schedule?.assistanceCheck?.checkDate ||
      b.schedule?.exactDate ||
      b.checkIn?.checkDate;
    if (!dateAStr || !dateBStr) return 0;
    return (
      moment.utc(dateAStr).startOf("day").valueOf() -
      moment.utc(dateBStr).startOf("day").valueOf()
    );
  });
}

/**
 * Incidencias guardadas en BD tienen prioridad sobre las calculadas en vivo (misma clave horario+estatus).
 * Así las resueltas conservan id numérico y acción final en nómina.
 */
export function mergePayrollPartnerIncidentsForTotal(
  savedRaw: any[],
  rangeData: { incidencias?: any[]; noProgramados?: any[] }
): any[] {
  const savedFiltered = savedRaw || [];
  const savedByKey = new Map<string, any>();
  for (const inc of savedFiltered) {
    const key = incidentRowKey(inc);
    if (key && inc?.schedule?.id) {
      savedByKey.set(key, inc);
    }
  }

  const seenLive = new Set<string>();
  const liveToAdd = (rangeData?.incidencias || []).filter((incident: any) => {
    if (!incident?.status || !incident?.schedule?.id) return false;
    const key = incidentRowKey(incident);
    if (!key || savedByKey.has(key) || seenLive.has(key)) return false;
    seenLive.add(key);
    return true;
  });

  const unscheduleRows = dedupeUnschedule(rangeData?.noProgramados || [])
    .filter((np) => {
      const checkYmd = moment(np.checkIn?.checkDate).format("YYYY-MM-DD");
      const alreadySaved = savedFiltered.some((inc: any) => {
        if (inc.status !== "No programado") return false;
        const savedYmd = moment(
          inc.schedule?.exactDate || inc.checkIn?.checkDate
        ).format("YYYY-MM-DD");
        return savedYmd === checkYmd;
      });
      return !alreadySaved;
    })
    .map(mapNoProgramadoToPayrollIncident);

  return [...savedFiltered, ...liveToAdd, ...unscheduleRows];
}

export async function fetchPayrollPartnerIncidentsForTotal(params: {
  partnerId: number;
  branchId: number;
  startDate: string;
  endDate: string;
  scheduleIds: number[];
}): Promise<any[]> {
  const { partnerId, branchId, startDate, endDate } = params;

  // Programados: guardados en BD + calculados en vivo (retardo, salió antes, etc.) vía POST /incident/schedules.
  // No programados: chequeos sin horario vía GET /incident/partner-no-programados.
  const [noProgramados, programadosRaw] = await Promise.all([
    findPartnerNoProgramadosPayroll(
      partnerId,
      branchId,
      startDate,
      endDate,
      true
    ),
    findIncidentsBySchedules([], {
      includePendingLeaderReview: true,
      partnerId,
      branchId,
      startDate,
      endDate,
      allBranches: true,
    }),
  ]);

  return sortIncidentsByDate(
    mergePayrollPartnerIncidentsForTotal(programadosRaw, {
      incidencias: [],
      noProgramados,
    })
  );
}
